/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineArrowLeft,
  HiOutlinePencil,
  HiOutlineRefresh,
  HiOutlineTrash,
} from "react-icons/hi";
import { FaRegCheckSquare, FaRegSquare } from "react-icons/fa";

import ListPage from "./ListPage";
import { useAdminContext } from "../context/admin/AdminContext";
import { useDoSeeDataContext } from "../context/DoSeeDataContext";
// @ts-ignore — JS helper
import { useGeoPositionContext } from "../pages/doseeData/utils/geoPosition";
// @ts-ignore — existing JSX component
import PromoteContent from "../pages/doseeData/utils/PromoteContent";
import { _PAGE_SIZE_ } from "../lib/other/tools";
import { Button, Card, ConfirmDialog, IconButton } from "../ui";
import type { Action, Column } from "../ui";

type AnyRow = Record<string, any>;

type DoSeePageProps<T extends AnyRow> = {
  /** _TYPES_.X constant the DoSeeData context keys by. */
  type: string;
  /** Page title. */
  title: string;
  /** Short subtitle shown under the icon+title. */
  subtitle?: string;
  /** Small icon in the header. */
  icon?: React.ReactNode;

  addRoute: string;
  editRoute: string;
  /** Middleware helper that stores the selected row for the edit screen. */
  pushOne: (row: T) => void;

  /** Keys the context exposes for this resource. */
  dataKey: string;
  countKey: string;
  idField: string;
  /** Defaults to "title". */
  titleField?: string;

  /** Extra columns rendered after the Title + Slug default columns. */
  extraColumns?: Column<T>[];

  /** Fields the search should match. Defaults to [titleField]. */
  searchFields?: string[];

  /** Set true for the 3 CRUD-only pages (Product, TourPackage, doseeAds). */
  simpleCrud?: boolean;
};

const STATUS_ACTIVE = 1;
const PROMOTED_NULL = new Set(["yes", "no", null, undefined, ""]);

const buildPromotedPatch = <T extends AnyRow>(
  row: T,
  nextPromoted: string | number,
): AnyRow => ({
  country_id: row.country_id,
  description: row.description,
  first_level_id: row.first_level_id,
  fourth_level_id: row.fourth_level_id,
  second_level_id: row.second_level_id,
  third_level_id: row.third_level_id,
  promoted: `${nextPromoted}`,
  slug: row.slug,
  status: row.status,
  thumbnail: row.thumbnail,
  image_cdn_id: row.image_cdn_id,
  title: row.title ?? row.name,
  url_images: row.url_images,
});

const buildStatusPatch = <T extends AnyRow>(row: T): AnyRow => ({
  country_id: row.country_id,
  description: row.description,
  first_level_id: row.first_level_id,
  fourth_level_id: row.fourth_level_id,
  second_level_id: row.second_level_id,
  third_level_id: row.third_level_id,
  slug: row.slug,
  status: row.status === STATUS_ACTIVE ? 0 : 1,
  thumbnail: row.thumbnail,
  title: row.title ?? row.name,
  url_images: row.url_images,
});

function DoSeeListPage<T extends AnyRow>({
  type,
  title,
  subtitle,
  icon,
  addRoute,
  editRoute,
  pushOne,
  dataKey,
  countKey,
  idField,
  titleField = "title",
  extraColumns = [],
  searchFields,
  simpleCrud = false,
}: DoSeePageProps<T>) {
  const navigate = useNavigate();
  const { sysAdmin } = useAdminContext();
  const isSuperAdmin = ["super_admin", "admin"].includes(sysAdmin?.role);

  const ctx: any = useDoSeeDataContext();
  const list: T[] | null | undefined = ctx[dataKey];
  const count: number | null | undefined = ctx[countKey];
  const { fetchDoSeeData, deleteOneContent, updateOneContent } = ctx;

  const geo: any = useGeoPositionContext();
  const {
    country_id,
    entry_label,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = geo ?? {};

  const requestBody = useMemo(
    () => ({
      country_id,
      first_level_id,
      second_level_id,
      third_level_id,
      fourth_level_id,
      content: type,
    }),
    [country_id, first_level_id, second_level_id, third_level_id, fourth_level_id, type],
  );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const [promoteRow, setPromoteRow] = useState<T | null>(null);
  const [promoteLoader, setPromoteLoader] = useState(false);
  const [suspendRow, setSuspendRow] = useState<T | null>(null);

  useEffect(() => {
    if (!list) fetchDoSeeData(requestBody, page, pageSize);
  }, [list]);

  const reloadData = () => fetchDoSeeData(requestBody, page, pageSize);

  const effectiveSearchFields = searchFields ?? [titleField];

  const rows = useMemo<T[] | null>(() => {
    if (!Array.isArray(list)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((row) =>
      effectiveSearchFields.some((field) =>
        String(row?.[field] ?? "").toLowerCase().includes(q),
      ),
    );
  }, [list, query, effectiveSearchFields.join(",")]);

  // Prune selection to visible rows.
  const visibleIds = useMemo(
    () => (rows ?? []).map((r) => r?.[idField]).filter(Boolean),
    [rows, idField],
  );
  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => visibleIds.includes(id)));
  }, [visibleIds]);

  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleAll = () => {
    setSelectedIds((prev) =>
      allSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds])),
    );
  };

  const toggleOne = (id: string | number) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const deleteMany = async () => {
    setBulkLoading(true);
    for (const id of selectedIds) {
      await deleteOneContent({ data_id: id, content: type });
    }
    setBulkLoading(false);
    setShowBulkDelete(false);
    setSelectedIds([]);
    reloadData();
  };

  const doDeleteOne = async (row: T) => {
    const { status } = await deleteOneContent({
      data_id: row[idField],
      content: type,
    });
    if (status === 200) reloadData();
  };

  const doSuspend = async () => {
    if (!suspendRow) return;
    const { status } = await updateOneContent({
      data_id: suspendRow[idField],
      data: buildStatusPatch(suspendRow),
      content: type,
    });
    setSuspendRow(null);
    if (status === 200) reloadData();
  };

  const doPromote = async (position: number | string) => {
    if (!promoteRow) return;
    setPromoteLoader(true);
    const previous = (list as T[] | undefined)?.find(
      (r) => r?.promoted === String(position),
    );
    const { status } = await updateOneContent({
      data_id: promoteRow[idField],
      data: buildPromotedPatch(promoteRow, position),
      content: type,
    });
    if (previous) {
      await updateOneContent({
        data_id: previous[idField],
        data: buildPromotedPatch(previous, "no"),
        content: type,
      });
    }
    setPromoteLoader(false);
    setPromoteRow(null);
    if (status === 200) reloadData();
  };

  const columns: Column<T>[] = [
    {
      header: "Select",
      className: "w-16",
      cell: (row) => (
        <IconButton
          aria-label={
            selectedIds.includes(row[idField]) ? "Unselect" : "Select"
          }
          variant="ghost"
          size="sm"
          onClick={() => toggleOne(row[idField])}
        >
          {selectedIds.includes(row[idField]) ? (
            <FaRegCheckSquare className="h-4 w-4 text-brand" />
          ) : (
            <FaRegSquare className="h-4 w-4 text-gray-400" />
          )}
        </IconButton>
      ),
    },
    {
      header: titleField === "name" ? "Name" : "Title",
      cell: (row) => (
        <span className="font-medium text-gray-900">
          {row?.[titleField] ?? "—"}
        </span>
      ),
    },
    ...extraColumns,
  ];

  if (!extraColumns.length && "slug" in (list?.[0] ?? {})) {
    columns.splice(2, 0, {
      header: "Slug",
      cell: (row) => (
        <span className="truncate text-[12.5px] text-gray-500">
          {row?.slug ?? "—"}
        </span>
      ),
    });
  }

  const rowActions = (row: T): Action[] => {
    const actions: Action[] = [];

    if (!simpleCrud && isSuperAdmin) {
      const isActive = row?.status === STATUS_ACTIVE;
      actions.push({
        label: isActive ? "Suspend" : "Unsuspend",
        variant: "secondary",
        onClick: () => setSuspendRow(row),
      });

      const isPromoted = !PROMOTED_NULL.has(row?.promoted);
      actions.push({
        label: isPromoted ? "Unpromote" : "Promote",
        variant: "secondary",
        onClick: () => setPromoteRow(row),
      });
    }

    actions.push({
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOne(row);
        navigate(editRoute);
      },
    });

    actions.push({
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => doDeleteOne(row),
      confirm: {
        title: `Delete ${row?.[titleField] ?? "this item"}?`,
        description: "This content will be permanently removed.",
        confirmLabel: "Delete",
      },
    });

    return actions;
  };

  const bulkToolbar = (
    <Card padding="sm" className="flex flex-wrap items-center gap-2">
      <Button
        variant="secondary"
        size="sm"
        onClick={toggleAll}
        disabled={visibleIds.length === 0}
      >
        {allSelected ? (
          <FaRegCheckSquare className="h-3.5 w-3.5 text-brand" />
        ) : (
          <FaRegSquare className="h-3.5 w-3.5" />
        )}
        {allSelected ? "Unselect all" : "Select all"}
      </Button>

      <Button
        variant={selectedIds.length > 0 ? "destructive" : "secondary"}
        size="sm"
        onClick={() => selectedIds.length > 0 && setShowBulkDelete(true)}
        disabled={selectedIds.length === 0 || bulkLoading}
      >
        <HiOutlineTrash className="h-3.5 w-3.5" />
        {selectedIds.length > 0
          ? `Delete ${selectedIds.length} ${selectedIds.length === 1 ? "item" : "items"}`
          : "Delete selected"}
      </Button>

      <Button variant="ghost" size="sm" onClick={reloadData}>
        <HiOutlineRefresh className="h-3.5 w-3.5" />
        Refresh
      </Button>

      {entry_label ? (
        <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-brand/5 px-2.5 py-1 text-[11px] font-medium text-brand ring-1 ring-inset ring-brand/15">
          Scope: {entry_label}
        </span>
      ) : null}
    </Card>
  );

  const pageActions: Action[] = [];
  if (country_id) {
    pageActions.push({
      label: `Add ${title.replace(/s$/, "") || "item"}`,
      variant: "primary",
      href: addRoute,
    });
  }
  pageActions.push({
    label: "Back",
    icon: <HiOutlineArrowLeft className="h-3.5 w-3.5" />,
    onClick: () => navigate("/dosee/data"),
  });

  const promoteCandidates = useMemo(() => {
    if (!Array.isArray(list)) return [];
    return list
      .filter((r: any) => !PROMOTED_NULL.has(r?.promoted))
      .map((r: any) => ({
        id: r?.[idField],
        title: r?.[titleField],
        position: parseInt(r?.promoted, 10),
      }));
  }, [list, idField, titleField]);

  const isSuspendActive = suspendRow?.status === STATUS_ACTIVE;

  return (
    <>
      <ListPage<T>
        title={title}
        subtitle={subtitle}
        icon={icon}
        actions={pageActions}
        toolbar={bulkToolbar}
        rows={rows}
        columns={columns}
        rowKey={(r) => r[idField]}
        rowActions={rowActions}
        search={{
          value: query,
          onChange: setQuery,
          placeholder: `Search ${title.toLowerCase()}…`,
        }}
        pagination={{
          page,
          pageSize,
          total: count,
          onChange: ({ page: p, pageSize: ps }) => {
            setPage(p);
            setPageSize(ps);
            fetchDoSeeData(requestBody, p, ps);
          },
        }}
        empty={`No ${title.toLowerCase()} in this scope yet.`}
      />

      <ConfirmDialog
        open={showBulkDelete}
        destructive
        title={`Delete ${selectedIds.length} ${selectedIds.length === 1 ? "item" : "items"}?`}
        description="The selected content will be permanently removed."
        confirmLabel={bulkLoading ? "Deleting…" : "Delete"}
        onCancel={() => setShowBulkDelete(false)}
        onConfirm={deleteMany}
      />

      <ConfirmDialog
        open={Boolean(suspendRow)}
        title={isSuspendActive ? "Suspend this item?" : "Unsuspend this item?"}
        description={
          isSuspendActive
            ? "It will be hidden from public listings until re-enabled."
            : "It will become visible on the public site again."
        }
        confirmLabel={isSuspendActive ? "Suspend" : "Unsuspend"}
        onCancel={() => setSuspendRow(null)}
        onConfirm={doSuspend}
      />

      <PromoteContent
        visible={Boolean(promoteRow)}
        setVisible={(v: boolean) => !v && setPromoteRow(null)}
        loading={promoteLoader}
        ids={promoteCandidates}
        onClick={(position: number | string) => doPromote(position)}
      />
    </>
  );
}

export default DoSeeListPage;
